import { useEffect, useRef, useState } from 'react'
import { Clock, Check } from 'lucide-react'

interface Props {
  /** Total seconds for the countdown (default 120 = 2 min) */
  durationSeconds?: number
  /** Called when user clicks "I'm here" */
  onDismiss: () => void
  /** Called when countdown hits 0 */
  onTimeout: () => void
}

export default function InactivityWarningModal({
  durationSeconds = 120,
  onDismiss,
  onTimeout,
}: Props) {
  const [remaining, setRemaining] = useState(durationSeconds)
  const deadlineRef = useRef<number>(Date.now() + durationSeconds * 1000)
  const firedRef = useRef(false)

  useEffect(() => {
    // Anchor to a fixed wall-clock deadline. Background tabs throttle timers, so
    // we compute remaining time from Date.now() rather than counting ticks — the
    // countdown can't be stretched by minimizing/switching windows.
    deadlineRef.current = Date.now() + durationSeconds * 1000

    const tick = () => {
      const secs = Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000))
      setRemaining(secs)
      if (secs <= 0 && !firedRef.current) {
        firedRef.current = true
        onTimeout()
      }
    }

    const id = setInterval(tick, 250)
    // Catch up the instant the tab regains visibility/focus (throttled background
    // ticks may have lagged) so logout fires promptly when the agent returns.
    const onVisible = () => { if (!document.hidden) tick() }
    document.addEventListener('visibilitychange', onVisible)
    window.addEventListener('focus', onVisible)
    tick()

    return () => {
      clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
      window.removeEventListener('focus', onVisible)
    }
  }, [durationSeconds, onTimeout])

  const mins = String(Math.floor(remaining / 60)).padStart(2, '0')
  const secs = String(remaining % 60).padStart(2, '0')

  // SVG ring
  const RADIUS = 52
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS
  const pct = remaining / durationSeconds
  const offset = CIRCUMFERENCE * (1 - pct)

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Dimmed backdrop */}
      <div className="absolute inset-0 bg-black/45 backdrop-blur-[2px]" />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm p-7 text-center">
        {/* Icon */}
        <div className="w-12 h-12 rounded-2xl bg-red-50 text-red-500 flex items-center justify-center mx-auto mb-3">
          <Clock className="w-6 h-6" />
        </div>

        {/* Ring countdown */}
        <div className="relative inline-block my-1">
          <svg viewBox="0 0 120 120" width="120" height="120">
            <circle cx="60" cy="60" r={RADIUS} fill="none" stroke="#fdecec" strokeWidth="8" />
            <circle
              cx="60" cy="60" r={RADIUS}
              fill="none"
              stroke="#d9433f"
              strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={CIRCUMFERENCE}
              strokeDashoffset={offset}
              transform="rotate(-90 60 60)"
              style={{ transition: 'stroke-dashoffset 1s linear' }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-2xl font-extrabold text-red-600 tabular-nums">{mins}:{secs}</span>
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">remaining</span>
          </div>
        </div>

        <h3 className="text-lg font-extrabold text-gray-900 mt-2 mb-1">Still there?</h3>
        <p className="text-sm text-gray-500 leading-relaxed mb-5">
          You'll be logged out due to inactivity. Your session — and today's punch-in — will be recorded.
        </p>

        <button
          onClick={onDismiss}
          className="w-full inline-flex items-center justify-center gap-2 h-12 bg-brandColor text-white rounded-xl text-sm font-bold hover:bg-brand-hover transition-colors shadow-lg shadow-brandColor/20"
        >
          <Check className="w-4 h-4" /> I'm here — stay logged in
        </button>

        <div className="mt-3 text-xs text-gray-400">
          No response → automatic logout at <b className="text-red-500">00:00</b>
        </div>
      </div>
    </div>
  )
}

/* -----------------------------------------------------------------------
   Demo — static preview (no real timer, just visual)
   -------------------------------------------------------------------- */
export function InactivityWarningDemo() {
  const [show, setShow] = useState(true)
  if (!show) {
    return (
      <div className="p-8 text-center">
        <button onClick={() => setShow(true)} className="px-4 py-2 bg-brandColor text-white rounded-lg text-sm font-semibold">
          Show inactivity warning again
        </button>
      </div>
    )
  }
  return (
    <InactivityWarningModal
      onDismiss={() => setShow(false)}
      onTimeout={() => setShow(false)}
    />
  )
}
